import { ThemeOptions, createTheme } from '@mui/material/styles';
import styles from './mui.module.scss';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    secondary: string;
  }
}

const breakpoints = {
  values: {
    xs: parseInt(styles['breakpoint-xs'].replace(/\D/g, '')),
    sm: parseInt(styles['breakpoint-sm'].replace(/\D/g, '')),
    md: parseInt(styles['breakpoint-md'].replace(/\D/g, '')),
    lg: parseInt(styles['breakpoint-lg'].replace(/\D/g, '')),
    xl: parseInt(styles['breakpoint-xl'].replace(/\D/g, '')),
  }
};

const fontSizes = {
  desktopValues: {
    h1: styles['fontSize-Desktop-h1'],
    h2: styles['fontSize-Desktop-h2'],
    h3: styles['fontSize-Desktop-h3'],
    h4: styles['fontSize-Desktop-h4'],
    h5: styles['fontSize-Desktop-h5'],
    h6: styles['fontSize-Desktop-h6'],
    body: styles['fontSize-Desktop-body'],
    buttonMedium: styles['fontSize-Desktop-buttonMedium'],
  },
  mobileValues: {
    h1: styles['fontSize-Mobile-h1'],
    h2: styles['fontSize-Mobile-h2'],
    h3: styles['fontSize-Mobile-h3'],
    h4: styles['fontSize-Mobile-h4'],
    h5: styles['fontSize-Mobile-h5'],
    h6: styles['fontSize-Mobile-h6'],
    body: styles['fontSize-Mobile-body'],
    buttonMedium: styles['fontSize-Mobile-buttonMedium'],
  }
}
const lineHeights = {
  desktopValues: {
    h1: styles['lineHeights-Desktop-h1'],
    h2: styles['lineHeights-Desktop-h2'],
    h3: styles['lineHeights-Desktop-h3'],
    h4: styles['lineHeights-Desktop-h4'],
    h5: styles['lineHeights-Desktop-h5'],
    h6: styles['lineHeights-Desktop-h6'],
    body: styles['lineHeights-Desktop-body'],
    buttonMedium: styles['lineHeights-Desktop-buttonMedium'],
  },
  mobileValues: {
    h1: styles['lineHeights-Mobile-h1'],
    h2: styles['lineHeights-Mobile-h2'],
    h3: styles['lineHeights-Mobile-h3'],
    h4: styles['lineHeights-Mobile-h4'],
    h5: styles['lineHeights-Mobile-h5'],
    h6: styles['lineHeights-Mobile-h6'],
    body: styles['lineHeights-Mobile-body'],
    buttonMedium: styles['lineHeights-Mobile-buttonMedium'],
  }
}

const themeConfig: ThemeOptions = {
  breakpoints: {
    values: {
      xs: breakpoints.values.xs,
      sm: breakpoints.values.sm,
      md: breakpoints.values.md,
      lg: breakpoints.values.lg,
      xl: breakpoints.values.xl,
    },
  },
  palette: {
    primary: {
      main: styles.primary,
    },
    secondary: {
      main: styles.secondary,
    },
    error: {
      main: styles.error,
    },
    success: {
      main: styles.success,
    },
    text: {
      primary: styles['text-primary'],
      secondary: styles['text-secondary'],
    },
    background: {
      default: styles.background,
      secondary: styles['background-secondary'],
    },
    action: {
      active: styles['action-primary'],
      hover: styles['action-primary'],
      disabled: styles['action-disabled'],
    },
  },
  typography: {
    htmlFontSize: parseInt(styles['font-size'].replace(/\D/g, '')),
    fontSize: parseInt(styles['font-size'].replace(/\D/g, '')),
    fontFamily: styles['font-default'],
    fontWeightRegular: styles['font-weight-regular'],
    fontWeightMedium: styles['font-weight-medium'],
    fontWeightBold: styles['font-weight-bold'],
    h1: {
      color: styles['text-primary'],
      fontSize: fontSizes.desktopValues.h1,
      fontWeight: styles['font-weight-regular'],
      lineHeight: lineHeights.desktopValues.h1,
      [`@media screen and (max-width: ${breakpoints.values.lg}px)`]: {
        fontSize: fontSizes.mobileValues.h1,
        lineHeight: lineHeights.mobileValues.h1,
      },
    },
    h2: {
      color: styles['text-primary'],
      fontSize: fontSizes.desktopValues.h2,
      fontWeight: styles['font-weight-regular'],
      lineHeight: lineHeights.desktopValues.h2,
      [`@media screen and (max-width: ${breakpoints.values.lg}px)`]: {
        fontSize: fontSizes.mobileValues.h2,
        lineHeight: lineHeights.mobileValues.h2,
      },
    },
    h3: {
      color: styles['text-primary'],
      fontSize: fontSizes.desktopValues.h3,
      fontWeight: styles['font-weight-regular'],
      lineHeight: lineHeights.desktopValues.h3,
      [`@media screen and (max-width: ${breakpoints.values.lg}px)`]: {
        fontSize: fontSizes.mobileValues.h3,
        lineHeight: lineHeights.mobileValues.h3,
      },
    },
    h4: {
      color: styles['text-primary'],
      fontSize: fontSizes.desktopValues.h4,
      fontWeight: styles['font-weight-regular'],
      lineHeight: lineHeights.desktopValues.h4,
      [`@media screen and (max-width: ${breakpoints.values.lg}px)`]: {
        fontSize: fontSizes.mobileValues.h4,
        lineHeight: lineHeights.mobileValues.h4,
      },
    },
    h5: {
      color: styles['text-primary'],
      fontSize: fontSizes.desktopValues.h5,
      fontWeight: styles['font-weight-regular'],
      lineHeight: lineHeights.desktopValues.h5,
      [`@media screen and (max-width: ${breakpoints.values.lg}px)`]: {
        fontSize: fontSizes.mobileValues.h5,
        lineHeight: lineHeights.mobileValues.h5,
      },
    },
    h6: {
      color: styles['text-primary'],
      fontSize: fontSizes.desktopValues.h6,
      fontWeight: styles['font-weight-regular'],
      lineHeight: lineHeights.desktopValues.h6,
      [`@media screen and (max-width: ${breakpoints.values.lg}px)`]: {
        fontSize: fontSizes.mobileValues.h6,
        lineHeight: lineHeights.mobileValues.h6
      },
    },
    body1: {
      fontSize: 16,
      lineHeight: '26px',
      fontWeight: styles['font-weight-regular'],
      color: styles['text-primary'],
    },
    body2: {
      fontSize: 16,
      lineHeight: '26px',
      fontWeight: styles['font-weight-regular'],
      color: styles['text-secondary'],
    },
    button: {
      fontSize: 14,
      lineHeight: '22px',
      fontWeight: styles['font-weight-regular'],
      textTransform: 'none',
      color: styles['text-primary'],
    },
    caption: { //Microcopy
      fontSize: 12,
    },
    subtitle1: {
      fontFamily: styles['font-default'],
      fontSize: 16,
      color: styles['text-secondary'],
    },
    subtitle2: {
      fontSize: 16,
      color: styles['text-primary'],
      fontWeight: 400,
    },
  },
  components: {
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: "#B5B6C0",
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: styles.primary,
        },
      },
    },
    MuiAutocomplete : { //Source : https://github.com/mui/material-ui/blob/master/packages/mui-material/src/Autocomplete/Autocomplete.js
      styleOverrides: {
        inputRoot: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: styles.primary,
          },
        },
        option: {
          "&.Mui-focused ": {
            backgroundColor: "#F3E7D9 !important",
          },
        },
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          padding: '8px 16px 8px 16px',
          "&.Mui-disabled": {
            background: "#7D7F90",
            border: '1px solid #7D7F90',
            color: "#FFFFFF"
          }
        },
        contained: {
          [`@media screen and (min-width: ${breakpoints.values.lg}px)`]: {
            width: '150px',
            height: '40px',
          },
          color: "white",
          boxShadow: 'none',
          border: '1px solid #1D1D1D',
          '&:hover': {
            background: "white",
            color: "#1D1D1D",
            boxShadow: 'none',
          },
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '100%',
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: '#1D1D1D',
          }
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#777777",
        },
        asterisk: {
          color: '#FF3030'
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: styles['text-primary']
        }
      }
    }
  },
};

const theme = createTheme(themeConfig);

export {
  theme
};
